/**
 * @file: This file opens and closes a native dialog from one flag.
 *
 * A page holds a boolean for the dialog it owns, and the browser holds the open
 * state of the `dialog` element. The two have to stay together. The operator
 * can also close a dialog with Escape, and the browser does that without the
 * page. This directive is the one place that keeps the flag and the element in
 * step, so no page writes showModal() again.
 *
 * The attribute takes an expression:
 *
 *   <dialog class="jumbotron-dialog" jumbotron-dialog="$ctrl.helpOpen">
 *
 * A second attribute is optional. It runs when the browser closes the dialog,
 * and it tells the page what the operator did:
 *
 *   <dialog class="jumbotron-dialog" jumbotron-dialog="$ctrl.report"
 *     jumbotron-dialog-close="$ctrl.close()">
 *
 * The directive writes a false to the first expression when it can, and then it
 * runs the second one. A page whose state is not a flag of its own therefore
 * needs no adapter on its controller.
 *
 * A click on the dimmed page also closes the dialog. The browser sends that
 * click to the dialog element, and the parts of the card are children of it.
 * The pointer down says where the action started, so a drag that starts on text
 * in the card and ends on the dimmed page keeps the dialog open.
 *
 * The directive has no scope of its own on purpose. An isolate scope would put
 * the content of the dialog on that scope, and the bindings of the page inside
 * the dialog would stop working.
 */

import angular from 'angular'

function jumbotronDialog ($parse) {
  'ngInject'

  return {
    restrict: 'A',
    link ($scope, $element, attrs) {
      const dialog = $element[0]
      const flag = $parse(attrs.jumbotronDialog)
      const closeExpression = attrs.jumbotronDialogClose
      let startedOnDialog = false

      function show () {
        if (dialog.open) {
          return
        }

        dialog.showModal()
        resetScroll()
      }

      function hide () {
        if (dialog.open) {
          dialog.close()
        }
      }

      // A dialog keeps its content between two opens, and the browser keeps the
      // scroll position with it. The operator opens a report and reads it from
      // the first line, so the body starts at the top every time.
      function resetScroll () {
        dialog.querySelectorAll('.jumbotron-dialog-body').forEach((body) => {
          body.scrollTop = 0
        })
      }

      /**
       * Tell the page that the dialog is closed. The flag is written first, so
       * the expression of the page reads the new state. An expression that
       * cannot be written is left alone.
       *
       * A page that closed the dialog itself already knows. Its flag is false
       * by now, and it must not hear the close a second time.
       */
      function reportClose () {
        if (!flag($scope)) {
          return
        }

        if (flag.assign) {
          flag.assign($scope, false)
        }

        if (closeExpression) {
          $scope.$eval(closeExpression)
        }
      }

      // Escape closes the dialog in the browser, so the page has to follow the
      // element. The event arrives outside a digest.
      function onClose () {
        $scope.$applyAsync(reportClose)
      }

      // A click on the dialog element itself is a click outside the card. It
      // counts only when the pointer went down there as well. close() then fires
      // the close event, which tells the page.
      function onPointerDown (event) {
        startedOnDialog = event.target === dialog
      }

      function onClick (event) {
        if (startedOnDialog && event.target === dialog) {
          hide()
        }
      }

      $scope.$watch(flag, (wanted) => (wanted ? show() : hide()))
      dialog.addEventListener('close', onClose)
      dialog.addEventListener('pointerdown', onPointerDown)
      dialog.addEventListener('click', onClick)

      // A route change can remove an open dialog. The page still waits for an
      // answer, so it hears the close before the element goes away.
      $scope.$on('$destroy', () => {
        if (dialog.open) {
          reportClose()
        }

        dialog.removeEventListener('close', onClose)
        dialog.removeEventListener('pointerdown', onPointerDown)
        dialog.removeEventListener('click', onClick)
        hide()
      })
    }
  }
}

export default angular.module('jumbotron-dialog.directive', [])
  .directive('jumbotronDialog', jumbotronDialog)
  .name

export { jumbotronDialog }
